import {
  ArrowRight,
  ClipboardCheck,
  HardHat,
  ShieldCheck,
  TestTube2,
} from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { PageHeroCard } from "@/components/site/PageHeroCard";
import { Button } from "@/components/ui/button";
import { useLang } from "@/i18n/LangContext";
import { companyBookingHref, companyInquiryHref, companyPaths } from "@/data/company";
import companiesHero from "@/assets/companies-hero.jpg";
import { PageMeta } from "@/components/site/PageMeta";

const entryIcons = [HardHat, TestTube2, ClipboardCheck] as const;
const assuranceIcons = [ShieldCheck, TestTube2] as const;

const Foretag = () => {
  const { t, lang } = useLang();
  const c = t.companies;
  const bookingHref = companyBookingHref(lang);
  const entryHrefs = {
    arbetsmiljo: companyPaths.arbetsmiljo[lang],
    drogtestning: companyPaths.drogtestning[lang],
    tjanster: companyPaths.tjanster[lang],
  } as const;


  return (
    <div className="min-h-screen bg-background">
      <PageMeta title={c.metaTitle} description={c.metaDescription} />
      <Header />
      <main>
        <PageHeroCard imageSrc={companiesHero} imageAlt={c.heroImageAlt} compact>
          <div className="animate-fade-up max-w-2xl">
            <span className="inline-flex h-7 items-center justify-center rounded-full bg-white/20 px-3.5 text-xs font-semibold uppercase tracking-wider text-white backdrop-blur-sm">
              {c.eyebrow}
            </span>
            <h1 className="mt-4 text-balance font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              {c.title}
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">{c.subtitle}</p>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
              <Button asChild variant="hero" size="xl" className="w-full sm:w-auto">
                <a href={bookingHref}>
                  {c.bookCta}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
              <Button asChild variant="ghost-outline" size="xl" className="w-full sm:w-auto">
                <a href={companyInquiryHref(lang)}>{c.inquiryCta}</a>
              </Button>
            </div>
          </div>
        </PageHeroCard>

        <section className="py-14 lg:py-20" aria-label={c.entriesAria}>
          <div className="container-wide">
            <div className="grid gap-5 md:grid-cols-3">
              {c.entries.map((entry, i) => {
                const Icon = entryIcons[i] ?? ClipboardCheck;
                return (
                  <a
                    key={entry.title}
                    href={entryHrefs[entry.hrefKey]}
                    className="group flex flex-col rounded-2xl border border-border/60 bg-card p-7 sm:rounded-3xl shadow-card transition-smooth hover:-translate-y-1 hover:border-primary/30 hover:shadow-elevated"
                  >
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                      <Icon className="h-6 w-6" />
                    </div>
                    <h2 className="mt-5 font-display text-xl font-semibold text-foreground">{entry.title}</h2>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">{entry.text}</p>
                    <span className="mt-5 inline-flex items-center text-sm font-medium text-primary">
                      {entry.linkLabel}
                      <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </a>
                );
              })}
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {c.assurance.map((item, i) => {
                const Icon = assuranceIcons[i] ?? ShieldCheck;
                return (
                  <div
                    key={item.title}
                    className="flex items-start gap-4 rounded-2xl border border-border/60 bg-card/80 sm:rounded-3xl px-6 py-5 shadow-card"
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                      <Icon className="h-5 w-5" />
                    </div>
                    <p className="text-sm leading-relaxed">
                      <strong className="block font-semibold text-foreground">{item.title}</strong>
                      <span className="text-muted-foreground">{item.text}</span>
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="pb-14 lg:pb-20">
          <div className="container-wide">
            <div className="mx-auto max-w-3xl text-center">
              <span className="text-sm font-semibold uppercase tracking-wider text-primary">
                {c.processEyebrow}
              </span>
              <h2 className="mt-2 font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
                {c.processTitle}
              </h2>
            </div>
            <ol className="mt-12 grid gap-6 md:grid-cols-3">
              {c.steps.map((step) => (
                <li
                  key={step.number}
                  className="rounded-2xl border border-border/60 bg-card p-7 sm:rounded-3xl shadow-card"
                >
                  <span className="font-display text-sm font-semibold tracking-wider text-primary">
                    {step.number}
                  </span>
                  <h3 className="mt-3 font-display text-xl font-semibold text-foreground">{step.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{step.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="px-2 py-16 sm:px-3 lg:px-4 lg:py-20">
          <div className="relative mx-auto w-full max-w-[96rem] overflow-hidden rounded-2xl bg-primary px-8 py-14 text-primary-foreground shadow-elevated sm:rounded-3xl lg:flex lg:items-center lg:justify-between lg:gap-10 lg:px-16 lg:py-20">
            <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
            <div className="absolute -bottom-24 -left-10 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
            <div className="relative max-w-2xl">
              <span className="text-sm font-semibold uppercase tracking-wider text-primary-foreground/80">
                {c.ctaEyebrow}
              </span>
              <h2 className="mt-2 font-display text-3xl font-semibold tracking-tight sm:text-4xl">
                {c.ctaTitle}
              </h2>
              <p className="mt-3 text-base text-primary-foreground/85">{c.ctaText}</p>
            </div>
            <div className="relative mt-6 shrink-0 lg:mt-0">
              <Button asChild variant="ghostOnPrimary" size="xl">
                <a href={companyInquiryHref(lang)}>
                  {c.inquiryCta}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </div>
          </div>
        </section>

        <Breadcrumbs
          items={[
            { label: lang === "sv" ? "Hem" : "Home", to: lang === "sv" ? "/" : "/en" },
            { label: t.nav.company },
          ]}
        />
      </main>
      <Footer />
    </div>
  );
};

export default Foretag;
